import { useEffect, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import api from "../lib/api";
import ListenButton from "../components/ListenButton";

export default function Tutor() {
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            text: "Hi — I'm your reading tutor. Ask me anything. How a word sounds, what a sentence means, why English spells things the way it does. No question is too small. You're doing great just by being here.",
        },
    ]);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [sending, setSending] = useState(false);
    const endRef = useRef(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, sending]);

    const send = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || sending) return;
        setMessages((m) => [...m, { role: "user", text }]);
        setInput("");
        setSending(true);
        try {
            const { data } = await api.post("/tutor/chat", { message: text, session_id: sessionId });
            setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
        } catch (err) {
            setMessages((m) => [
                ...m,
                { role: "assistant", text: "I couldn't reach the tutor just now. Please try again in a moment." },
            ]);
        } finally {
            setSending(false);
        }
    };

    const suggestions = [
        "How do I sound out the word 'through'?",
        "What's the difference between /b/ and /p/?",
        "Can you explain silent e with simple examples?",
    ];

    return (
        <main className="container-page py-12 max-w-3xl fade-up">
            <p className="label-eyebrow mb-2">Your tutor</p>
            <h1 className="text-4xl font-semibold text-[#1C2321] mb-3" style={{ fontFamily: "Outfit" }} data-testid="tutor-title">
                Ask anything about reading
            </h1>
            <p className="text-lg text-[#515B57] mb-8">
                A patient, plainspoken tutor. Powered by AI, here whenever you need it.
            </p>

            <div className="card-rw p-6 md:p-8 mb-5 min-h-[420px] max-h-[520px] overflow-y-auto" data-testid="tutor-conversation">
                <div className="space-y-5">
                    {messages.map((m, i) => (
                        <div
                            key={i}
                            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            data-testid={`tutor-msg-${m.role}-${i}`}
                        >
                            <div
                                className={`max-w-[85%] px-5 py-4 rounded-2xl ${
                                    m.role === "user"
                                        ? "bg-[#2D5D4E] text-[#FAF9F6] rounded-br-md"
                                        : "bg-[#E8E2D2] text-[#1C2321] rounded-bl-md"
                                }`}
                            >
                                <p className="text-base leading-relaxed whitespace-pre-line">{m.text}</p>
                                {m.role === "assistant" && (
                                    <div className="mt-3">
                                        <ListenButton
                                            text={m.text.slice(0, 380)}
                                            label="Listen"
                                            size="sm"
                                            testId={`tutor-listen-${i}`}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {sending && (
                        <div className="flex items-center gap-2 text-[#819888]" data-testid="tutor-typing">
                            <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
                        </div>
                    )}
                    <div ref={endRef} />
                </div>
            </div>

            {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 mb-5">
                    {suggestions.map((s, i) => (
                        <button
                            key={i}
                            onClick={() => setInput(s)}
                            data-testid={`tutor-suggest-${i}`}
                            className="px-4 py-2 rounded-full border border-[#E5E7E2] bg-white text-sm text-[#515B57] hover:bg-[#E8E2D2]"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}

            <form onSubmit={send} className="flex gap-3" data-testid="tutor-form">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your question…"
                    data-testid="tutor-input"
                    className="flex-1 px-5 py-3.5 rounded-lg border border-[#E5E7E2] bg-white text-lg focus:outline-none focus:ring-2 focus:ring-[#2D5D4E]"
                />
                <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    data-testid="tutor-send-button"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237] disabled:opacity-50"
                >
                    <Send className="w-5 h-5" /> Send
                </button>
            </form>
        </main>
    );
}
